import { zodResolver } from "@hookform/resolvers/zod";
import {
  type FieldArrayWithId,
  type UseFieldArrayRemove,
  useFieldArray,
  useForm,
} from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { CalendarIcon, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/ajax/queryClient";
import { useState } from "react";
import { useTRPC } from "@/trpc";
import DatePicker from "@/components/DatePicker";
import { SaveButton } from "@/components/Button/SaveButton";
import { USStateSelect } from "@/components/Selects/USStateSelect";
import { Switch } from "@/components/ui/switch";
import type { SaveCredentialFieldInputValue } from "@/features/credential-fields/types/cedential-field";

const licenseSchema = z.object({
  state: z.string().length(2, "State code must be 2 characters"),
  license_number: z
    .string()
    .max(255, "License number must be less than 255 characters"),
  expiration_date: z.date(),
  deleted: z.boolean().optional(),
});

const formSchema = z.object({
  stateLicenses: z.array(licenseSchema),
  imlcLicense: z.object({
    has_license: z.boolean(),
    state_codes: z.array(z.string()),
    expiration_date: z.date().nullable(),
  }),
});

type FormValues = z.infer<typeof formSchema>;
type StateLicenseValue = Omit<
  Partial<z.infer<typeof licenseSchema>>,
  "expiration_date"
> & {
  expiration_date?: string | Date;
};
type ImlcLicenseValue = z.infer<typeof formSchema>["imlcLicense"];
type CredentialFieldEntry<TValue = unknown> = {
  index: number;
  value: TValue | null;
};

export function StateLicensesForm({
  onSave,
  stateLicenses,
  imlcLicense,
  userId,
}: {
  onSave: () => void;
  stateLicenses: CredentialFieldEntry[];
  imlcLicense: Partial<ImlcLicenseValue> | null;
  userId: string;
}) {
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const trpc = useTRPC();

  const mutation = useMutation(
    trpc.credentialFiles.saveCredentialsFields.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.credentialFiles.getCredentialsFields.queryKey(),
        });
        setSaveStatus("idle");
        setErrorMessage(null);
        onSave();
      },
      onError: (error) => {
        setSaveStatus("error");
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to save changes",
        );
        setTimeout(() => setSaveStatus("idle"), 4000);
      },
    }),
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      stateLicenses:
        stateLicenses
          .filter((val) => val.value)
          .map((val) => {
            const value = val.value as StateLicenseValue;
            return {
              state: value.state || "",
              license_number: value.license_number || "",
              expiration_date: value.expiration_date
                ? new Date(value.expiration_date)
                : new Date(),
            };
          }) || [],
      imlcLicense: {
        has_license: imlcLicense?.has_license || false,
        state_codes: imlcLicense?.state_codes || [],
        expiration_date: imlcLicense?.expiration_date
          ? new Date(imlcLicense.expiration_date)
          : null,
      },
    },
  });

  const {
    fields: licenseFields,
    append: appendLicense,
    remove: removeLicense,
  } = useFieldArray({
    control: form.control,
    name: "stateLicenses",
  });

  function onSubmit(data: FormValues) {
    setSaveStatus("saving");
    setErrorMessage(null);

    const existingIndexes = (stateLicenses || [])
      .filter((val) => val.value)
      .map((val) => val.index);
    const newIndexMax = data.stateLicenses.length;

    const fieldValues: SaveCredentialFieldInputValue[] = [
      ...data.stateLicenses.map((license, index) => ({
        fieldCode: "state_medical_licenses",
        value: {
          state: license.state,
          license_number: license.license_number,
          expiration_date: format(license.expiration_date, "yyyy-MM-dd"),
        },
        index: index + 1,
        delete: false,
      })),

      ...existingIndexes
        .filter((index: number) => index > newIndexMax)
        .map((index: number) => ({
          fieldCode: "state_medical_licenses",
          value: null,
          delete: true,
          index,
        })),

      // Add IMLC license data
      {
        fieldCode: "imlc_license",
        value: {
          has_license: data.imlcLicense.has_license,
          state_codes: data.imlcLicense.state_codes,
          expiration_date: data.imlcLicense.expiration_date
            ? format(data.imlcLicense.expiration_date, "yyyy-MM-dd")
            : null,
        },
        index: 1,
        delete: false,
      },
    ];

    mutation.mutate({
      fieldValues,
      switchUserId: userId,
    });
  }

  const renderLicenseFields = (
    fields: FieldArrayWithId<FormValues, "stateLicenses", "id">[],
    remove: UseFieldArrayRemove,
  ) => {
    return fields.map((field, index) => (
      <div key={field.id} className="space-y-4 p-4 border rounded-lg relative">
        <div className="flex justify-end mb-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              remove(index as number);
            }}
          >
            Remove
          </Button>
        </div>

        <FormField
          control={form.control}
          name={`stateLicenses.${index}.state`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>State</FormLabel>
              <FormControl>
                <USStateSelect {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`stateLicenses.${index}.license_number`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>License Number</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={`stateLicenses.${index}.expiration_date`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expiration Date</FormLabel>
              <div className="relative">
                <DatePicker
                  selected={field.value}
                  onChange={(date) => field.onChange(date)}
                  customInput={
                    <Input
                      value={
                        field.value ? format(field.value, "MM-dd-yyyy") : ""
                      }
                    />
                  }
                  dateFormat="MM-dd-yyyy"
                  wrapperClassName="w-full"
                />
                <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    ));
  };

  return (
    <Form {...form}>
      {errorMessage && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-6 w-6" />
          <AlertDescription className="ml-2">{errorMessage}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* IMLC License Section */}
        <div className="space-y-4 p-4 border rounded-lg">
          <FormField
            control={form.control}
            name="imlcLicense.has_license"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel>I have an IMLC License</FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {form.watch("imlcLicense.has_license") && (
            <>
              <FormField
                control={form.control}
                name="imlcLicense.expiration_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiration Date</FormLabel>
                    <div className="relative">
                      <DatePicker
                        selected={field.value}
                        onChange={(date) => field.onChange(date)}
                        customInput={
                          <Input
                            value={
                              field.value
                                ? format(field.value, "MM-dd-yyyy")
                                : ""
                            }
                          />
                        }
                        dateFormat="MM-dd-yyyy"
                        wrapperClassName="w-full"
                      />
                      <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <div className="space-y-4">
              {renderLicenseFields(licenseFields, removeLicense)}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  appendLicense({
                    state: "",
                    license_number: "",
                    expiration_date: new Date(),
                  })
                }
              >
                Add State License
              </Button>
            </div>
          </div>
        </div>

        <SaveButton saveStatus={saveStatus} />
        <Button onClick={onSave} className="w-full p-6" variant="outline">
          Cancel
        </Button>
      </form>
    </Form>
  );
}
